import { useState, useEffect, useCallback } from 'react';
import { usePharma } from '../../../providers/PharmaProvider';
import { ProtocolConfigResponse } from '../types/protocolRuleset';

const API_URL = 'https://dev.seevividly.com/api_clinical_trials/get_protocol_config';

/**
 * Fetches the server-side protocol ruleset (source of truth for protocol
 * values). The study is resolved server-side from the account's flags, so no
 * study parameter is needed. Returns null until loaded; callers fall back to
 * the static SIENNA config via applyProtocolRuleset().
 */
export const useProtocolConfig = () => {
  const { user } = usePharma();
  const [data, setData] = useState<ProtocolConfigResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    if (!user?.user_name || !user?.p) {
      setData(null);
      setError(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          u: user.user_name,
          p: user.p,
        }),
      });

      const responseText = await response.text();
      if (!response.ok) {
        console.error("Protocol Config API Error Response:");
        throw new Error(`Protocol Config API request failed: ${response.status} ${response.statusText || ''}.`);
      }

      let result;
      try {
        result = JSON.parse(responseText);
      } catch (parseError: any) {
        console.error("Failed to parse Protocol Config API response:");
        throw new Error(`Invalid JSON received from Protocol Config API. ${parseError.message}`);
      }

      if (result?.status === 'success' && result.data?.ruleset) {
        setData(result.data as ProtocolConfigResponse);
      } else {
        console.error('Unexpected Protocol Config API response format:');
        setError('Received invalid data format from Protocol Config API.');
        setData(null);
      }
    } catch (err: any) {
      console.error('Fetch protocol config error:');
      setError(err.message || 'Failed to load protocol config.');
      setData(null);
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, isLoading, error, refetch: fetchData };
};
